import { Reducer } from 'redux'
import { DateTime } from 'luxon'
import { PropensityActionTypes } from './action'
import { LastEvaluatedObject } from '@labrav/marketplace'

export enum ScoreStatus {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

export enum InferenceLimitOperations {
  SET = 'set',
  INCREASE = 'increase',
  DECREASE = 'decrease',
}

export enum PageVariant {
  LOCKED = 'locked',
  INFO = 'info',
  PTBLOCK = 'ptb',
  ASSESSMENTS = 'Assessments',
  DEFAULT = 'default',
}

export enum Marketplace {
  AWS = 'AWS',
  AZURE = 'Microsoft',
  GCP = 'GCP',
  ALL = 'All',
}
export enum LastUpdated {
  ALL = 'all',
  TWENTY_FOUR_HOURS = '24hours',
  SEVEN_DAYS = '7days',
  THIRTY_DAYS = '30days',
}

export enum PropensityScore {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
  ALL = 'All',
}

export interface ChatCTA {
  label: string
  prompt: string
}

export type ChatRole = 'USER' | 'ASSISTANT' | 'AGENT_RESPONSE'

export type AgentStage =
  | 'thinking'
  | 'retrieving'
  | 'using_tool'
  | 'processing'
  | 'responding'
  | 'complete'

export interface CompletedStage {
  stage: AgentStage
  message: string
  timestamp: string
}

export interface Chat {
  eventId: string
  role: ChatRole
  content: string
  timestamp: string
  ctas?: ChatCTA[]
  agentStage?: AgentStage
  stageMessage?: string
  completedStages?: CompletedStage[]
  error?: string
}
export interface FilterType {
  time_range?: string
  marketplaces?: string
  propensity_level?: string
  aws_level?: string
  azure_level?: string
  gcp_level?: string
  userId?: string
  icp_qualified?: boolean
  is_crm?: boolean
  expected_revenue_min?: number
  expected_revenue_max?: number
}

export interface InferenceLimitUpdate {
  operation: InferenceLimitOperations
  value: number | 'unlimited'
}
export interface PropensityTier {
  [key: string]: string | number
}

export interface OpportunityItem {
  opportunityId?: string
  opportunityName?: string
  opportunityLink?: string
  expectedRevenue?: {
    currency: string
    amount: number
  }
}

export interface Limit {
  limit: number | string | null
  utilized_limit: number | null
  archived_count: number | null
}

export interface SellerUrlUpdate {
  seller_url: string | null
}

export interface OnboardingStatusResponse {
  partner_id: string
  onboarded: boolean
}

export interface Conversation {
  id: string
  conversationTitle: string
  createdAt: string
  updatedAt: string
  sessionId: string
  userId: string
}

export interface ConversationResponse {
  conversations: Conversation[]
  page: number
  page_size: number
  total: number
}

export interface AccountPipelineHealth {
  domainName: string
  highPercentage: number
  lowPercentage: number
  mediumPercentage: number
}

export interface PropensityRow {
  id: string
  inferenceBatchId: string
  domainName: string
  opportunityId?: string
  userName?: string
  userMail?: string
  domainLink?: string
  aws: PropensityTier
  azure: PropensityTier
  gcp: PropensityTier
  partnerId: string
  propensity: string
  prospect: string
  seller: string
  timestamp: string
  companyName: string
  industry: string
  criticalOpportunityCount?: number
  marketplacePresence: string | Record<string, string>
  opportunityName?: string
  opportunities?: OpportunityItem[]
  expectedRevenue?: {
    currency: string
    amount: number
  }
  icpQualified?: boolean
  icpName?: string
  icpQualifiedCount?: number
  icpFitLevel?: string
  icpFit?: number
  accountPipelineHealth?: AccountPipelineHealth
  icpQualificationResults?: {
    icpId?: string
    icpName?: string
    qualified?: boolean
    qualificationLevel?: string
    score?: number
    matchMessages?: string[]
    fitLevel?: string
    fitScore?: number
    icpFit?: number
  }[]
}

export interface FiltersState {
  marketplace: Marketplace[]
  lastUpdated: string
  propensityScore: PropensityScore[]
  aws: ScoreStatus[]
  azure: ScoreStatus[]
  gcp: ScoreStatus[]
  icpQualified?: boolean | null
  isCrm?: boolean | null
  expectedRevenueMin?: number | null
  expectedRevenueMax?: number | null
}

export interface RecommendationItem {
  id: string
  title: string
  prompt: string
  iconType?: string
  displayOrder?: number
  toolTip?: string
  aiAssistantMessage?: string
  extraData?: Record<string, any>
}

export interface SmartFlowItem extends RecommendationItem {
  flowType?: string
}

export interface Recommendations {
  partnerId: string
  recommendationDate: string
  fastActions: RecommendationItem[]
  askLabraSuggestions: RecommendationItem[]
  smartFlows: SmartFlowItem[]
}

export interface UserConfiguration {
  isDefault: boolean
  id: string | null
  partnerId: string
  userId: string
  viewKey: string | null
  columnsConfig: ColumnConfig[]
  icpIds: string[]
  createdAt: string | null
  updatedAt: string | null
}

export interface ColumnConfig {
  columnKey: string
  isActive: boolean
  order: number
}
export interface PropensityData {
  records: PropensityRow[]
  pageSize: number
  pageNumber: number
  noOfPage: number | null
  lastEvaluatedObject?: LastEvaluatedObject | null
  count?: number
  domainUrls?: string[]
  dropDownKey?: string
  showDefault?: boolean
  showAlert?: boolean
  promptInput?: string
  activeSessionId?: string
  conversations?: Conversation[]
  inferenceLimit?: Limit | null
  chatNextToken?: string | null
  seller?: boolean
  chats?: Chat[]
  filters: FiltersState
  uploadedByUserMails?: string[]
  platformUsersFromPtb?: Array<{
    user_mail: string
    user_name: string | null
  }>
  archived?: boolean
  hasMoreChats?: boolean
  conversationsPage?: number
  conversationsPageSize?: number
  conversationsTotal?: number
  recommendations?: Recommendations | null
  hasOpenedAiDrawer?: boolean
  isExternalLoading?: boolean
  userConfigurations?: UserConfiguration | null
}
export interface PropensityPayload {
  records: PropensityRow[]
  count: number
  pageSize: number
}
type actionType =
  | {
      type: PropensityActionTypes.SET_REPORTS_DATA
      payload: PropensityPayload
    }
  | {
      type: PropensityActionTypes.PROPENSITY_SET_PAGE_NUMBER
      payload: number
    }
  | {
      type: PropensityActionTypes.PROPENSITY_SET_LIMIT
      payload: number
    }
  | {
      type: PropensityActionTypes.CLEAR_PROPENSITY_REPORTS
    }
  | {
      type: PropensityActionTypes.SET_FILTERS
      payload: FiltersState
    }
  | {
      type: PropensityActionTypes.SET_DROP_DOWN_KEY
      payload: string
    }
  | {
      type: PropensityActionTypes.CLEAR_DROP_DOWN_KEY
    }
  | {
      type: PropensityActionTypes.SHOW_ALERT
    }
  | {
      type: PropensityActionTypes.HIDE_ALERT
    }
  | {
      type: PropensityActionTypes.SHOW_DEFAULT
    }
  | {
      type: PropensityActionTypes.HIDE_DEFAULT
    }
  | {
      type: PropensityActionTypes.CLEAR_PROPENSITY_FILTERS
    }
  | {
      type: PropensityActionTypes.SET_DOMAIN_URLS
      payload: string[]
    }
  | {
      type: PropensityActionTypes.CLEAR_DOMAIN_URLS
    }
  | {
      type: PropensityActionTypes.SET_INFERENCE_LIMIT
      payload: Limit
    }
  | {
      type: PropensityActionTypes.SET_SELLER
      payload: boolean
    }
  | {
      type: PropensityActionTypes.SET_PROMPT_INPUT
      payload: string
    }
  | {
      type: PropensityActionTypes.CLEAR_PROMPT_INPUT
    }
  | {
      type: PropensityActionTypes.SET_CHATS
      payload: Chat[]
    }
  | {
      type: PropensityActionTypes.ADD_CHAT
      payload: Chat
    }
  | {
      type: PropensityActionTypes.UPDATE_CHAT
      payload: { eventId: string; updates: Partial<Omit<Chat, 'eventId'>> }
    }
  | {
      type: PropensityActionTypes.CLEAR_CHATS
    }
  | {
      type: PropensityActionTypes.SET_ACTIVE_SESSION_ID
      payload: string
    }
  | {
      type: PropensityActionTypes.CLEAR_ACTIVE_SESSION_ID
    }
  | {
      type: PropensityActionTypes.SET_CONVERSATIONS
      payload: Conversation[]
    }
  | {
      type: PropensityActionTypes.APPEND_CONVERSATIONS
      payload: Conversation[]
    }
  | {
      type: PropensityActionTypes.CLEAR_CONVERSATIONS
    }
  | {
      type: PropensityActionTypes.SET_CONVERSATIONS_PAGE_INFO
      payload: { page: number; pageSize: number; total: number }
    }
  | {
      type: PropensityActionTypes.SET_CHAT_NEXT_TOKEN
      payload: string
    }
  | {
      type: PropensityActionTypes.CLEAR_CHAT_NEXT_TOKEN
    }
  | {
      type: PropensityActionTypes.PREPEND_CHATS
      payload: Chat[]
    }
  | {
      type: PropensityActionTypes.SET_HAS_MORE_CHATS
      payload: boolean
    }
  | {
      type: PropensityActionTypes.SET_RECOMMENDATIONS
      payload: Recommendations | null
    }
  | {
      type: PropensityActionTypes.CLEAR_RECOMMENDATIONS
    }
  | {
      type: PropensityActionTypes.SET_HAS_OPENED_AI_DRAWER
      payload: boolean
    }
  | {
      type: PropensityActionTypes.SET_IS_EXTERNAL_LOADING
      payload: boolean
    }
  | {
      type: PropensityActionTypes.CLEAR_IS_EXTERNAL_LOADING
    }
  | {
      type: PropensityActionTypes.SET_USER_CONFIGURATIONS
      payload: UserConfiguration
    }
  | {
      type: PropensityActionTypes.SET_UPLOADED_BY_USER_MAILS
      payload: string[]
    }
  | {
      type: PropensityActionTypes.SET_PLATFORM_USERS_FROM_PTB
      payload: Array<{
        user_mail: string
        user_name: string | null
      }>
    }
  | {
      type: PropensityActionTypes.SET_ARCHIVED_FLAG
      payload: boolean
    }

export const initialFilterPropensityState: FiltersState = {
  marketplace: [Marketplace.ALL],
  lastUpdated: LastUpdated.ALL,
  propensityScore: [PropensityScore.ALL],
  aws: [],
  azure: [],
  gcp: [],
  icpQualified: null,
  isCrm: null,
  expectedRevenueMin: null,
  expectedRevenueMax: null,
}

export const initialState: PropensityData = {
  records: [],
  pageSize: 10,
  pageNumber: 1,
  noOfPage: null,
  lastEvaluatedObject: null,
  count: 0,
  showDefault: true,
  showAlert: false,
  domainUrls: [],
  dropDownKey: '',
  inferenceLimit: null,
  seller: false,
  promptInput: '',
  activeSessionId: '',
  chats: [],
  chatNextToken: null,
  hasMoreChats: true,
  filters: {
    ...initialFilterPropensityState,
  },
  uploadedByUserMails: [],
  platformUsersFromPtb: [],
  archived: false,
  conversationsPage: 1,
  conversationsPageSize: 15,
  conversationsTotal: 0,
  hasOpenedAiDrawer: false,
  isExternalLoading: false,
  userConfigurations: {
    isDefault: true,
    id: null,
    partnerId: '',
    userId: '',
    viewKey: null,
    columnsConfig: [],
    icpIds: [],
    createdAt: null,
    updatedAt: null,
  },
}
export const reducer: Reducer<PropensityData, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case PropensityActionTypes.SET_REPORTS_DATA: {
      const payload = action.payload as PropensityPayload & {
        appendRecords?: boolean
      }
      const {
        appendRecords,
        records: incomingRecords,
        count,
        pageSize,
        ...restPayload
      } = payload
      const nextRecords =
        appendRecords === true
          ? [...state.records, ...incomingRecords]
          : [...incomingRecords]
      return {
        ...state,
        ...restPayload,
        count,
        pageSize,
        records: nextRecords,
        noOfPage: Math.ceil(count / pageSize),
      }
    }
    case PropensityActionTypes.PROPENSITY_SET_LIMIT: {
      return {
        ...state,
        pageSize: action.payload,
      }
    }
    case PropensityActionTypes.PROPENSITY_SET_PAGE_NUMBER: {
      return {
        ...state,
        pageNumber: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_PROPENSITY_REPORTS: {
      return {
        ...state,
        records: [],
        count: 0,
        lastEvaluatedObject: null,
      }
    }
    case PropensityActionTypes.SET_FILTERS: {
      return {
        ...state,
        filters: action.payload,
        lastEvaluatedObject: null,
      }
    }
    case PropensityActionTypes.CLEAR_PROPENSITY_FILTERS: {
      return {
        ...state,
        filters: { ...initialFilterPropensityState },
        lastEvaluatedObject: null,
      }
    }
    case PropensityActionTypes.SET_DROP_DOWN_KEY: {
      return {
        ...state,
        dropDownKey: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_DROP_DOWN_KEY: {
      return {
        ...state,
        dropDownKey: '',
      }
    }
    case PropensityActionTypes.SHOW_DEFAULT: {
      return {
        ...state,
        showDefault: true,
      }
    }
    case PropensityActionTypes.HIDE_DEFAULT: {
      return {
        ...state,
        showDefault: false,
      }
    }
    case PropensityActionTypes.SHOW_ALERT: {
      return {
        ...state,
        showAlert: true,
      }
    }
    case PropensityActionTypes.HIDE_ALERT: {
      return {
        ...state,
        showAlert: false,
      }
    }

    case PropensityActionTypes.SET_DOMAIN_URLS: {
      return {
        ...state,
        domainUrls: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_DOMAIN_URLS: {
      return {
        ...state,
        domainUrls: [],
      }
    }
    case PropensityActionTypes.SET_INFERENCE_LIMIT: {
      return {
        ...state,
        inferenceLimit: action.payload,
      }
    }
    case PropensityActionTypes.SET_SELLER: {
      return {
        ...state,
        seller: action.payload,
      }
    }
    case PropensityActionTypes.SET_PROMPT_INPUT: {
      return {
        ...state,
        promptInput: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_PROMPT_INPUT: {
      return {
        ...state,
        promptInput: '',
      }
    }
    case PropensityActionTypes.SET_CHATS: {
      return {
        ...state,
        chats: action.payload,
      }
    }
    case PropensityActionTypes.ADD_CHAT: {
      return {
        ...state,
        chats: [...(state.chats || []), action.payload],
      }
    }
    case PropensityActionTypes.UPDATE_CHAT: {
      return {
        ...state,
        chats: (state.chats || []).map(chat =>
          chat.eventId === action.payload.eventId
            ? { ...chat, ...action.payload.updates }
            : chat
        ),
      }
    }
    case PropensityActionTypes.CLEAR_CHATS: {
      return {
        ...state,
        chats: [],
      }
    }
    case PropensityActionTypes.SET_ACTIVE_SESSION_ID: {
      return {
        ...state,
        activeSessionId: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_ACTIVE_SESSION_ID: {
      return {
        ...state,
        activeSessionId: '',
      }
    }
    case PropensityActionTypes.SET_CONVERSATIONS: {
      return {
        ...state,
        conversations: action.payload,
      }
    }
    case PropensityActionTypes.APPEND_CONVERSATIONS: {
      return {
        ...state,
        conversations: [...(state.conversations || []), ...action.payload],
      }
    }
    case PropensityActionTypes.CLEAR_CONVERSATIONS: {
      return {
        ...state,
        conversations: [],
      }
    }
    case PropensityActionTypes.SET_CONVERSATIONS_PAGE_INFO: {
      return {
        ...state,
        conversationsPage: action.payload.page,
        conversationsPageSize: action.payload.pageSize,
        conversationsTotal: action.payload.total,
      }
    }
    case PropensityActionTypes.SET_CHAT_NEXT_TOKEN: {
      return {
        ...state,
        chatNextToken: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_CHAT_NEXT_TOKEN: {
      return {
        ...state,
        chatNextToken: null,
      }
    }
    case PropensityActionTypes.PREPEND_CHATS: {
      return {
        ...state,
        chats: [...action.payload, ...(state.chats || [])],
      }
    }
    case PropensityActionTypes.SET_HAS_MORE_CHATS: {
      return {
        ...state,
        hasMoreChats: action.payload,
      }
    }
    case PropensityActionTypes.SET_RECOMMENDATIONS: {
      return {
        ...state,
        recommendations: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_RECOMMENDATIONS: {
      return {
        ...state,
        recommendations: null,
      }
    }
    case PropensityActionTypes.SET_HAS_OPENED_AI_DRAWER: {
      return {
        ...state,
        hasOpenedAiDrawer: action.payload,
      }
    }
    case PropensityActionTypes.SET_IS_EXTERNAL_LOADING: {
      return {
        ...state,
        isExternalLoading: action.payload,
      }
    }
    case PropensityActionTypes.CLEAR_IS_EXTERNAL_LOADING: {
      return {
        ...state,
        isExternalLoading: false,
      }
    }
    case PropensityActionTypes.SET_USER_CONFIGURATIONS: {
      return {
        ...state,
        userConfigurations: action.payload,
      }
    }
    case PropensityActionTypes.SET_UPLOADED_BY_USER_MAILS: {
      return {
        ...state,
        uploadedByUserMails: action.payload,
      }
    }
    case PropensityActionTypes.SET_PLATFORM_USERS_FROM_PTB: {
      return {
        ...state,
        platformUsersFromPtb: action.payload,
      }
    }
    case PropensityActionTypes.SET_ARCHIVED_FLAG: {
      return {
        ...state,
        archived: action.payload,
      }
    }
    default: {
      return state
    }
  }
}
